import React from "react";
import {
  createStyles,
  Box,
  Stack,
  Text,
  Center,
  Image,
  Space,
  Group,
  Anchor,
  List,
  Button,
  Accordion,
} from "@mantine/core";

import { Link, useNavigate } from "react-router-dom";

function SupportPage({ children, ...otherProps }) {
  const { classes } = useStyles();
  const navigation = useNavigate();
  return (
    <Box className={classes.container} {...otherProps}>
      {/* Header */}
      <Box className={classes.root}>
        <Box className={classes.grid}>
          <Box className={classes.section}>
            <Center px={50} pt={30} pb={10}>
              <Stack spacing={0} justify='center'>
                <Center>
                  <Group spacing={10} align="center" sx={{ cursor: "pointer" }} onClick={() => navigation("/")}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(135deg, #5622F4, #7B4DFF)",
                      }}
                    >
                      <Image
                        src="/mineralis-ico.svg"
                        alt="Mineralis"
                        width={30}
                        height={30}
                        sx={{ objectFit: "contain" }}
                      />
                    </Box>
                    <Text weight={800} size={30} sx={{ color: "#5622F4" }}>
                      Mineralis
                    </Text>
                  </Group>
                </Center>
              </Stack>
            </Center>
          </Box>
          <Space h={30} />
        </Box>
      </Box>

      <Box className={classes.containerTwo}>
        <Box className={classes.root}>
          <Box className={classes.grid}>
            <Box className={classes.section} mt={50} px={25}>
              <Stack spacing={0} justify='left'>
                <Text weight={600} size={26} sx={{ color: "#000000" }}>
                  Support Center
                </Text>
                <Text weight={400} size={18} sx={{ color: "#87808A" }}>
                  Frequently Asked Questions
                </Text>
              </Stack>

              <Accordion multiple={true}>
                <Accordion.Item value='How do I identify a mineral'>
                  <Accordion.Control>
                    <Text weight={600} size='lg'>
                      How do I identify a mineral?
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <List type='ordered' withPadding>
                      <List.Item>Open the Mineralis app and tap the Scan tab.</List.Item>
                      <List.Item>Choose your scan type: Raw, Polished, or Jewelry.</List.Item>
                      <List.Item>Take a photo or select one from your library.</List.Item>
                      <List.Item>Wait a moment while AI analyzes your specimen.</List.Item>
                      <List.Item>View your identification result, alternatives, and detailed info.</List.Item>
                    </List>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value='How accurate is the AI identification'>
                  <Accordion.Control>
                    <Text weight={600} size='lg'>
                      How accurate is the AI identification?
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text weight={400}>
                      Mineralis uses advanced AI to provide highly accurate mineral identification.
                      Each result includes a confidence score and up to 2 alternative matches.
                      For best results, take clear, well-lit photos against a neutral background.
                    </Text>
                    <Text weight={400} mt={10}>
                      Keep in mind that AI identification is a guide and may not replace
                      professional geological analysis for rare or unusual specimens.
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value='Can I use the app offline'>
                  <Accordion.Control>
                    <Text weight={600} size='lg'>
                      Can I use the app offline?
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text weight={400}>
                      Your saved collection, notes, and wishlists are stored locally and are
                      available offline at any time.
                    </Text>
                    <Text weight={400} mt={10}>
                      The AI identification feature requires an internet connection to process
                      your photos. The Explore section also requires connectivity for the latest
                      guides and content.
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value='How do I cancel my subscription'>
                  <Accordion.Control>
                    <Text weight={600} size='lg'>
                      How do I cancel my subscription?
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <List type='ordered' withPadding>
                      <List.Item>Open the iOS Settings app.</List.Item>
                      <List.Item>Tap on your account name at the top.</List.Item>
                      <List.Item>Tap Subscriptions.</List.Item>
                      <List.Item>Tap on Mineralis in the list of subscriptions.</List.Item>
                      <List.Item>Tap Cancel Subscription.</List.Item>
                    </List>
                    For more information, please{" "}
                    <Anchor href='https://support.apple.com/en-us/118428' target='_blank' sx={{ color: "#5622F4" }}>
                      check here
                    </Anchor>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value='How do I export my collection'>
                  <Accordion.Control>
                    <Text weight={600} size='lg'>
                      How do I export my collection?
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text weight={400}>
                      Collection export is coming in a future update. We're working on the ability
                      to export your mineral collection as PDF or share individual entries.
                    </Text>
                    <Text weight={400} mt={10}>
                      In the meantime, you can view all your saved items in the Collection tab
                      and use iOS's built-in screenshot functionality to capture entries.
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value='What types of stones can Mineralis identify'>
                  <Accordion.Control>
                    <Text weight={600} size='lg'>
                      What types of stones can Mineralis identify?
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text weight={400}>
                      Mineralis can identify a wide range of specimens, including:
                    </Text>
                    <List withPadding mt={10}>
                      <List.Item><strong>Raw minerals</strong> — naturally occurring mineral specimens</List.Item>
                      <List.Item><strong>Polished stones</strong> — tumbled, cabochon, or carved pieces</List.Item>
                      <List.Item><strong>Jewelry stones</strong> — gemstone inserts in jewelry settings</List.Item>
                      <List.Item><strong>Crystals</strong> — quartz, amethyst, selenite, and more</List.Item>
                      <List.Item><strong>Gemstones</strong> — precious and semi-precious gems</List.Item>
                      <List.Item><strong>Rocks</strong> — igneous, sedimentary, and metamorphic rocks</List.Item>
                    </List>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value='Restoring a previous purchase'>
                  <Accordion.Control>
                    <Text weight={600} size='lg'>
                      Restoring a previous purchase
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text weight={400}>
                      If you are installing Mineralis on a new device, you may need to
                      restore a previous purchase.
                    </Text>
                    <List type='ordered' withPadding>
                      <List.Item>Tap the gear icon to open settings.</List.Item>
                      <List.Item>
                        Tap on "Upgrade to Premium" at the top of settings.
                      </List.Item>
                      <List.Item>Tap the "Restore" on the bottom.</List.Item>
                    </List>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value='Request a refund'>
                  <Accordion.Control>
                    <Text weight={600} size='lg'>
                      Request a refund
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <List type='ordered' withPadding>
                      <List.Item>
                        Sign in to{" "}
                        <Anchor href='https://reportaproblem.apple.com' target='_blank' sx={{ color: "#5622F4" }}>
                          reportaproblem.apple.com
                        </Anchor>
                      </List.Item>
                      <List.Item>
                        Tap or click "I'd like to," then choose "Request a refund."
                      </List.Item>
                      <List.Item>
                        Choose the reason why you want a refund, then choose Next.
                      </List.Item>
                      <List.Item>
                        Find the recent subscription item of the app Mineralis, then choose
                        Submit.
                      </List.Item>
                    </List>
                    <Text weight={400}>
                      If your request is approved by Apple, it might take additional time
                      for the funds to return to your payment method.
                    </Text>
                    <Text weight={400}>
                      For more information, please{" "}
                      <Anchor
                        href='https://support.apple.com/en-gb/HT204084'
                        target='_blank'
                        sx={{ color: "#5622F4" }}
                      >
                        check here
                      </Anchor>
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>

              <Stack spacing={10} justify='left' pl={20}>
                <Text weight={400} size={18} sx={{ color: "#87808A" }}>
                  Get in touch with us, we'd love to hear from you! We're here to help or
                  answer any additional questions.
                </Text>
                <Box mt={30}>
                  <Button
                    radius='xl'
                    size='md'
                    w={200}
                    onClick={() => navigation("/contact")}
                    sx={{
                      backgroundColor: "#5622F4",
                      "&:hover": { backgroundColor: "#4A1DC9" },
                    }}
                  >
                    Contact Us
                  </Button>
                </Box>
              </Stack>
            </Box>
            <Space h={30} />
            <Space h={440} />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.containerFooter}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={30}>
            <Box className={classes.sectionTwo}>
              <Text weight={400} size={16} align='left' sx={{ color: "#87808A" }}>
                Made with love by Anatoli Niculeac<sup>&copy;</sup> 2026
              </Text>
              <Group spacing={10} p={20} position='right'>
                <Anchor component={Link} to={"/support"} className={classes.link}>
                  <Text weight={500} size={16} align='left' sx={{ color: "#5622F4" }}>
                    Support
                  </Text>
                </Anchor>
                <Text weight={400} size={16} align='left' sx={{ color: "#87808A" }}>
                  |
                </Text>
                <Anchor component={Link} to={"/terms"} className={classes.link}>
                  <Text weight={500} size={16} align='left' sx={{ color: "#5622F4" }}>
                    Terms of use
                  </Text>
                </Anchor>
                <Text weight={400} size={16} align='left' sx={{ color: "#87808A" }}>
                  |
                </Text>
                <Anchor component={Link} to={"/privacy"} className={classes.link}>
                  <Text weight={500} size={16} align='left' sx={{ color: "#5622F4" }}>
                    Privacy Policy
                  </Text>
                </Anchor>
                <Text weight={400} size={16} align='left' sx={{ color: "#87808A" }}>
                  |
                </Text>
                <Anchor component={Link} to={"/contact"} className={classes.link}>
                  <Text weight={500} size={16} align='left' sx={{ color: "#5622F4" }}>
                    Contact
                  </Text>
                </Anchor>
              </Group>
              <Space h={10} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SupportPage;

const useStyles = createStyles({
  root: {
    height: "100%",
    width: "100%",
    overflowX: "hidden",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
  },
  container: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "100%",
    "@media (max-width: 768px)": { width: "100%" },
    width: "100%",
    height: "100%",
    background: "linear-gradient(180deg, #EDE8FE 0%, #F0ECFF 100%)",
  },
  containerTwo: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "100%",
    "@media (max-width: 768px)": { width: "100%" },
    width: "100%",
    height: "100%",
    backgroundColor: "#FFFFFF",
  },
  containerFooter: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "100%",
    "@media (max-width: 768px)": { width: "100%" },
    width: "100%",
    height: "100%",
    background: "linear-gradient(180deg, #F0ECFF 0%, #EDE8FE 100%)",
  },
  section: {
    maxWidth: 980,
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: 25,
    alignItems: "center",
    paddingLeft: 25,
    paddingRight: 25,
  },
  sectionTwo: {
    maxWidth: 980,
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: "1em",
    alignItems: "center",
    paddingLeft: 25,
    paddingRight: 25,
  },
  grid: {
    display: "grid",
    width: "100%",
    height: "100%",
    gridTemplateColumns: "auto",
    gridTemplateRows: "auto",
    "@media (max-width: 1023px)": {
      gridTemplateColumns: "auto",
      justifyContent: "stretch",
    },
    "@media (min-width: 1600px)": {
      gridTemplateColumns: "auto",
      gridTemplateRows: "auto",
      WebkitTransition: "300ms",
    },
  },
  link: {
    whiteSpace: "nowrap",
    textDecoration: "none",
    ":last-child": { borderRight: "none" },
    transition: "opacity 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)",
    ":hover": {
      textDecoration: "none",
      opacity: 0.7,
    },
  },
});
